import {
  DuctedCompatibilityState,
  DuctedRepresentativeData,
  DuctedRepresentativeEntries,
  DuctedResultSet
} from './ducted-compatibility-types';

export const emptyDuctedRepresentativeData: DuctedRepresentativeData = {
  combination: null,
  rank: null,
  productData: {},
  accessoryData: {},
  modelSpecs: {}
};

// The Recommended-row entries of one (system, resultSet) on a slice draft,
// created on first touch.
export const representativeEntriesFor = (
  state: DuctedCompatibilityState,
  systemUniqueId: string,
  resultSet: DuctedResultSet
): DuctedRepresentativeEntries => {
  if (!state.representativesBySystem[systemUniqueId]) {
    // eslint-disable-next-line no-param-reassign
    state.representativesBySystem[systemUniqueId] = {};
  }
  const bySystem = state.representativesBySystem[systemUniqueId];
  if (!bySystem[resultSet]) {
    bySystem[resultSet] = {};
  }
  return bySystem[resultSet]!;
};

// The Recommended rows a page-one request covers go loading under its hash:
// previous data stays under the loading state (a failed refresh keeps the
// last row), and the new hash becomes the only one finish/fail may apply.
export const beginRepresentatives = (
  entries: DuctedRepresentativeEntries,
  typeCodes: string[],
  requestBodyHash: string
): void => {
  typeCodes.forEach((typeCode) => {
    const current = entries[typeCode];
    // eslint-disable-next-line no-param-reassign
    entries[typeCode] = current
      ? { ...current, status: 'loading', requestBodyHash }
      : {
          ...emptyDuctedRepresentativeData,
          status: 'loading',
          requestBodyHash
        };
  });
};

// The rows the response carried land loaded; a type that went loading under
// this hash but came back with nothing lands as the honest empty row.
export const finishRepresentatives = (
  entries: DuctedRepresentativeEntries,
  representativesByType: Record<string, DuctedRepresentativeData>,
  requestBodyHash: string
): void => {
  Object.entries(representativesByType).forEach(([typeCode, data]) => {
    // eslint-disable-next-line no-param-reassign
    entries[typeCode] = { ...data, status: 'loaded', requestBodyHash };
  });
  Object.keys(entries).forEach((typeCode) => {
    const entry = entries[typeCode];
    if (
      entry.requestBodyHash === requestBodyHash &&
      entry.status === 'loading'
    ) {
      // eslint-disable-next-line no-param-reassign
      entries[typeCode] = {
        ...emptyDuctedRepresentativeData,
        status: 'loaded',
        requestBodyHash
      };
    }
  });
};

// A failed page-one request: the rows that rode it keep their last data
// under the error state.
export const failRepresentatives = (
  entries: DuctedRepresentativeEntries,
  requestBodyHash: string
): void => {
  Object.keys(entries).forEach((typeCode) => {
    if (entries[typeCode].requestBodyHash === requestBodyHash) {
      // eslint-disable-next-line no-param-reassign
      entries[typeCode] = { ...entries[typeCode], status: 'error' };
    }
  });
};
